/**
 * Tooltip copy for every computed field — straight from the Premium
 * Selection Figma (Tooltips frame). Kept in one table so copy reviews
 * touch a single file. `title` is the plain-language line, `formula`
 * renders below it (atlas Tooltip's `description` slot).
 */
export interface FieldTooltip {
    title: string;
    formula?: string;
}

export const FIELD_TOOLTIPS = {
    primaryPremium: {
        title: "Sum of maturity-adjusted premiums across all facilities.",
        formula: "Schedule → Maturity Adjusted Premium",
    },
    finalPremium: {
        title: "Selected premium + Tech/RM fee + TRIA + surplus lines tax + fee + Mcare fee + PCF surcharges",
    },
    // TODO(design): placeholder copy derived from the backend field
    // descriptions (PR #303) — pending the official wording from design.
    fullLossRatio: {
        title: "Loss ratio over the utilized loss years.",
        formula: "Loss excess SIR ÷ (selected premium × utilized loss years)",
    },
    cappedLossRatio5yr: {
        title: "Loss ratio over the most recent 5 report years.",
        formula: "5-year loss excess SIR ÷ (selected premium × 5)",
    },
    deviationFromUnity: {
        title: "How far the selected premium deviates from the model.",
        formula: "(Selected premium − total premium) ÷ total premium",
    },
    retroDateCreditPremium: {
        title: "Sum of maturity-adjusted premiums across all facilities.",
        formula: "Schedule → Maturity Adjusted Premium",
    },
    premiumWithSirCredit: {
        title: "Sum of SIR-adjusted premiums across all facilities.",
        formula: "Schedule → SIR Adjusted Premium",
    },
    qualityCreditDebit: {
        title: "Selected credit applied to premium w/ SIR credit",
        formula: "Premium w/ SIR credit × (1 − %)",
    },
    adjustedPremium: {
        title: "Result of the premium walk after all credits. Carries into additional coverages.",
        formula: "Premium w/ SIR credit × (1 − base rate) × (1 − quality) × (1 − experience) × (1 − corridor)",
    },
    totalPremium: {
        title: "Adjusted premium plus all additional coverages.",
        formula: "Adjusted premium + coverages",
    },
    totalCommission: {
        title: "Tricura commission + agent commission",
    },
    tria: {
        title: "Selected premium × 5% × TRIA rate",
    },
    surplusLinesTax: {
        title: "Rate varies by state",
        formula: "Selected premium × tax rate",
    },
    surplusLinesFee: {
        title: "Rate varies by state",
        formula: "Selected premium × fee rate",
    },
    probabilityToBind: {
        title: "Underwriter's likelihood the account binds.",
    },
    // TODO(design): placeholder copy for the redesigned top summary cards
    // (OR-1359) — pending official wording from design.
    selectedPremium: {
        title: "The premium the underwriter selected to bind on.",
    },
    sir: {
        title: "Self-insured retention selected for the account.",
    },
    expiringData: {
        title: "Prior-term premium and SIR for comparison at renewal.",
    },
} as const satisfies Record<string, FieldTooltip>;
